/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { UniversalTrackerValuePlain } from '../types/surveyScope';
import { UniversalTrackerPlain } from '../types/universalTracker';
import { InputColumn, TableColumn } from '../components/survey/form/input/table/InputInterface';


// This file is different from @link universalTrackerValue.tsx by focusing on
// extract values once questions is answered and is a copy of functions from API



/** Extract correct data property based on setup **/
export const getUtrvDataProp = (utrv: Pick<UniversalTrackerValuePlain, 'valueData'>, userInput = false) => {
  if (!utrv.valueData) {
    return undefined
  }

  if (!userInput || !utrv.valueData.input) {
    return utrv.valueData.data;
  }

  const inputData = utrv.valueData.input.data;
  if (utrv.valueData.isImported) {
    // [GU-4066] Partial input with empty input.table, check
    const hasRootData = typeof utrv.valueData.data === 'object' && Object.keys(utrv.valueData.data).length > 0;
    const hasInputData = typeof inputData === 'object' && Object.keys(inputData).length > 0;
    if (hasRootData && !hasInputData) {
      return utrv.valueData.data;
    }
  }

  return inputData;
}


export const getUtrvTableProp = (utrv: Pick<UniversalTrackerValuePlain, 'valueData'>, userInput = false) => {
  if (!utrv.valueData) {
    return undefined
  }

  // No input means it was not submitted by user, should fallback to available
  if (!userInput || !utrv.valueData.input) {
    return utrv.valueData.table;
  }

  const table = utrv.valueData.input.table;
  if (utrv.valueData.isImported) {
    // [GU-4066] Partial input with empty input.table, check
    const hasRootData = Array.isArray(utrv.valueData.table) && utrv.valueData.table.length > 0;
    const hasInputData = Array.isArray(table) && table.length > 0;
    if (hasRootData && !hasInputData) {
      return utrv.valueData.table; // Broken import situation
    }
  }


  return table;
}

export const getUtrvValue = (utrv: Pick<UniversalTrackerValuePlain, 'value' | 'valueData'>, userInput = false) => {
  if (!userInput || !utrv.valueData || !utrv.valueData.input) {
    return utrv.value;
  }

  // [GU-4066] Partial input without input.value
  if (utrv.valueData.isImported && utrv.valueData.input.value === undefined) {
    return utrv.value;
  }

  return utrv.valueData.input.value;
}

export const getUnitCode = (
  utrv: Pick<UniversalTrackerValuePlain, 'unit' | 'valueData'>,
  utr: Pick<UniversalTrackerPlain, 'unit'>,
  userInput = false
) => {
  if (!userInput || !utrv.valueData || !utrv.valueData.input) {
    // weird that say mention unit is deprecated on frontend, but seems like it's used properly
    return utrv.unit || utr.unit;
  }

  if (utrv.valueData.isImported && utrv.valueData.input.unit === undefined) {
    // [GU-4066] Partial input with empty input.table, check
    return utrv.unit || utr.unit;
  }

  return utrv.valueData.input.unit;
}

export const getNumberScaleCode = (
  utrv: Pick<UniversalTrackerValuePlain, 'numberScale' | 'valueData'>,
  utr: Pick<UniversalTrackerPlain, 'numberScale'>,
  userInput = false
) => {
  if (!userInput || !utrv.valueData || !utrv.valueData.input) {
    return utrv.numberScale || utr.numberScale;
  }
  if (utrv.valueData.isImported && utrv.valueData.input.numberScale === undefined) {
    // [GU-4066] Partial input with empty input.table, check
    return utrv.numberScale || utr.numberScale;
  }
  return utrv.valueData.input.numberScale;
}

export const getTableNumberScaleCode = (
  tableColumn: TableColumn,
  rowData: InputColumn,
  displayUserInput = false,
): string => {

  if (displayUserInput) {
    // Because we might be dealing with no-input data, fallback to column
    return rowData.numberScale ?? tableColumn.numberScale ?? ''
  }

  return tableColumn.numberScale ?? '';
}

export const getTableUnitCode = (
  tableColumn: TableColumn,
  rowData: InputColumn,
  displayUserInput = false,
): string => {
  if (displayUserInput) {
    // Because we might be dealing with no-input data, fallback to column
    return rowData.unit ?? tableColumn.unit ?? ''
  }
  return tableColumn.unit ?? '';
}

